import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorTextCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { validateEmail } from "common/validator";
import { useAppDispatch } from "hooks";
import { updateCompany } from "services/company.service";
import { CompanyEntity } from "types/Company";
import { fetchCompanyDetails } from "./settings-container.slice";
import {
  ChangeLink,
  EditPanel,
  SettingsCard,
  useSaveWithToast,
} from "./ui";

type CompanyDetails = {
  name: string;
  legalName: string;
  code: string;
  vat: string;
  address: string;
  contactEmail: string;
  contactPhone: string;
  bankName: string;
  bankIban: string;
  description: string;
};

type DetailField = {
  name: keyof CompanyDetails;
  labelKey: string;
  hintKey?: string;
  required?: boolean;
  wide?: boolean;
  maxLength?: number;
};

// Every detail, in the order it is shown and edited. A required one cannot be
// saved empty; a wide one takes the full row. The contact e-mail is also
// checked as an address, and the server can refuse it.
const DETAIL_FIELDS: DetailField[] = [
  {
    name: "name",
    labelKey: "settings_company_name_label",
    hintKey: "settings_company_name_hint",
    required: true,
  },
  {
    name: "legalName",
    labelKey: "settings_company_legal_name_label",
    hintKey: "settings_company_legal_name_hint",
  },
  { name: "code", labelKey: "settings_company_code_label", required: true },
  { name: "vat", labelKey: "settings_company_vat_label" },
  { name: "address", labelKey: "settings_company_address_label", required: true },
  {
    name: "contactEmail",
    labelKey: "settings_company_contact_email_label",
    hintKey: "settings_company_contact_email_hint",
  },
  {
    name: "contactPhone",
    labelKey: "settings_company_contact_phone_label",
    hintKey: "settings_company_contact_phone_hint",
    maxLength: 50,
  },
  { name: "bankName", labelKey: "settings_company_bank_label" },
  { name: "bankIban", labelKey: "settings_company_iban_label" },
  { name: "description", labelKey: "settings_company_description_label", wide: true },
];

// The server refuses a contact email it cannot send replies to with
// 400 `{ code: "INVALID_CONTACT_EMAIL" }`.
function isInvalidContactEmail(error: any): boolean {
  return error?.response?.data?.code === "INVALID_CONTACT_EMAIL";
}

function detailsOf(company: CompanyEntity): CompanyDetails {
  return {
    name: company.name,
    legalName: company.legalName ?? "",
    code: company.code,
    vat: company.vat ?? "",
    address: company.address,
    contactEmail: company.contactEmail ?? "",
    contactPhone: company.contactPhone ?? "",
    bankName: company.bankName ?? "",
    bankIban: company.bankIban ?? "",
    description: company.description ?? "",
  };
}

/** The company's own details; saving goes through the PATCH /:companyId the old modal used. */
export default function CompanyDetailsCard({
  company,
}: {
  company: CompanyEntity;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const details = detailsOf(company);

  return (
    <SettingsCard
      title={t("settings_company_details_title")}
      description={t("settings_company_details_lead")}
      actions={
        !editing && <ChangeLink label={t("edit")} onClick={() => setEditing(true)} />
      }
    >
      {editing ? (
        <CompanyDetailsForm company={company} onDone={() => setEditing(false)} />
      ) : (
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {DETAIL_FIELDS.map(({ name, labelKey, wide }) => (
            <div key={name} className={wide ? "sm:col-span-2" : undefined}>
              <dt className="text-xs text-gray-500">{t(labelKey)}</dt>
              <dd className="mt-0.5 whitespace-pre-line break-words text-sm text-gray-900">
                {details[name] || (
                  <span className="text-gray-500">{t("settings_none")}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </SettingsCard>
  );
}

// Mounted only while editing, so closing it throws away the draft and any
// error from the last attempt.
function CompanyDetailsForm({
  company,
  onDone,
}: {
  company: CompanyEntity;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(detailsOf(company));
  // The contact email the server refused; the error goes away once it is edited.
  const [rejectedEmail, setRejectedEmail] = useState<string | null>(null);
  // Empty required fields are flagged after the first try to save, not while
  // the person is still typing.
  const [triedToSave, setTriedToSave] = useState(false);

  const isMissing = (field: DetailField) =>
    field.required && !draft[field.name].trim();

  // An empty contact email is fine (replies then go to whoever prepared the invoice).
  const emailLooksWrong = () =>
    draft.contactEmail.trim() !== "" && !validateEmail(draft.contactEmail.trim());

  const contactEmailError =
    (triedToSave && emailLooksWrong()) || draft.contactEmail === rejectedEmail;
  // Which fields currently show an address error (only the contact email can).
  const addressError: Partial<Record<keyof CompanyDetails, boolean>> = {
    contactEmail: contactEmailError,
  };

  const submit = async () => {
    setTriedToSave(true);
    if (DETAIL_FIELDS.some(isMissing) || emailLooksWrong()) return;

    const saved = await save(
      async () => {
        try {
          await updateCompany(draft, company.id);
        } catch (err) {
          if (isInvalidContactEmail(err)) setRejectedEmail(draft.contactEmail);
          throw err;
        }
        await dispatch(fetchCompanyDetails({ companyId: company.id }));
      },
      {
        errorMessage: (err) =>
          isInvalidContactEmail(err)
            ? t("settings_company_contact_email_invalid")
            : t("settings_save_failed"),
      }
    );
    if (saved) onDone();
  };

  return (
    <EditPanel onSave={submit} onCancel={onDone} saving={saving} error={error}>
      <div className="grid gap-3 sm:grid-cols-2">
        {DETAIL_FIELDS.map((field) => {
          const id = `company-detail-${field.name}`;
          const missing = triedToSave && isMissing(field);
          const badAddress = addressError[field.name];
          const control = {
            id,
            className: inputCls,
            value: draft[field.name],
            "aria-invalid": missing || badAddress,
            onChange: (
              event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              setDraft({ ...draft, [field.name]: event.target.value });
            },
          };

          return (
            <div key={field.name} className={field.wide ? "sm:col-span-2" : undefined}>
              <label htmlFor={id} className={labelCls}>
                {t(field.labelKey)}
              </label>
              {field.wide ? (
                <textarea rows={3} {...control} />
              ) : (
                <input type="text" maxLength={field.maxLength} {...control} />
              )}
              {missing && (
                <p className={errorTextCls}>{t("settings_company_field_required")}</p>
              )}
              {badAddress && (
                <p className={errorTextCls}>
                  {t("settings_company_contact_email_invalid")}
                </p>
              )}
              {field.hintKey && (
                <p className={helpTextCls}>{t(field.hintKey)}</p>
              )}
            </div>
          );
        })}
      </div>
    </EditPanel>
  );
}
